import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ShieldStar, SignOut, MagnifyingGlass, Infinity as InfinityIcon,
  Clock, XCircle, CheckCircle, Lightning, ArrowCounterClockwise, Prohibit,
  Users as UsersIcon, Tag, UserPlus,
} from "@phosphor-icons/react";
import { LogoMark } from "@/components/Logo";
import AdminPlans from "@/components/AdminPlans";
import AdminLeads from "@/components/AdminLeads";

const PIN_STORAGE_KEY = "ventai_admin_pin";

function adminHeaders() {
  const pin = sessionStorage.getItem(PIN_STORAGE_KEY) || "";
  return { "X-Admin-PIN": pin };
}

function PinGate({ onUnlock }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/verify-pin", { pin });
      sessionStorage.setItem(PIN_STORAGE_KEY, pin);
      onUnlock();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PIN invalido");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#09090B] text-[#FAFAFA] flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="admin-pin-form">
        <div className="flex items-center gap-3">
          <div className="relative">
            <LogoMark size={48} />
            <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-[#DC2626] border-2 border-[#09090B] flex items-center justify-center">
              <ShieldStar size={10} weight="fill" className="text-white" />
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#71717A]">Acceso restringido</div>
            <h2 className="font-display text-2xl font-medium tracking-tight">Panel super admin</h2>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-[#71717A]">PIN de seguridad</Label>
          <Input
            type="password"
            inputMode="numeric"
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            data-testid="admin-pin-input"
            className="mt-2 bg-[#121214] border-[#27272A] focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626] h-11 tracking-[0.5em] text-center font-mono"
            placeholder="------"
          />
        </div>
        <Button
          type="submit"
          disabled={loading || !pin}
          data-testid="admin-pin-submit"
          className="w-full h-11 bg-[#DC2626] hover:bg-[#B91C1C] text-white"
        >
          {loading ? "Verificando..." : "Desbloquear"}
        </Button>
        <p className="text-[11px] text-[#71717A] text-center">
          Este panel solo es visible para el super admin autorizado.
        </p>
      </form>
    </div>
  );
}

function MetricCard({ label, value, tone = "neutral", icon: Icon }) {
  const tones = {
    neutral: "text-[#FAFAFA]",
    green: "text-[#22C55E]",
    amber: "text-[#F59E0B]",
    red: "text-[#EF4444]",
    violet: "text-[#A78BFA]",
  };
  return (
    <div className="bg-[#121214] border border-[#27272A] rounded-lg p-4 flex items-center gap-3">
      {Icon ? <Icon size={22} className={tones[tone]} /> : null}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{label}</div>
        <div className={`font-display text-2xl font-medium ${tones[tone]}`} data-testid={`metric-${label.toLowerCase().replace(/\s+/g,"-")}`}>
          {value}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status, days }) {
  if (status === "unlimited") {
    return <Badge className="bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/30 gap-1" data-testid="badge-unlimited"><InfinityIcon size={12} />Ilimitado</Badge>;
  }
  if (status === "active") {
    return <Badge className="bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30 gap-1" data-testid="badge-active"><Clock size={12} />Activo · {days}d</Badge>;
  }
  return <Badge className="bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/30 gap-1" data-testid="badge-expired"><XCircle size={12} />Expirado</Badge>;
}

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(!!sessionStorage.getItem(PIN_STORAGE_KEY));
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [extendTarget, setExtendTarget] = useState(null); // {user, mode: 'extend'|'reactivate'}
  const [extendDays, setExtendDays] = useState(7);
  const [tab, setTab] = useState("users"); // users | plans | leads
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [acting, setActing] = useState(false);

  // 404-like redirect for non-super-admins (defensive — route also enforces at App.js)
  useEffect(() => {
    if (user && !user.is_super_admin) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [m, u] = await Promise.all([
        api.get("/admin/metrics", { headers: adminHeaders() }),
        api.get("/admin/users", { headers: adminHeaders(), params: { q: query || undefined, status_filter: statusFilter } }),
      ]);
      setMetrics(m.data);
      setUsers(u.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        sessionStorage.removeItem(PIN_STORAGE_KEY);
        setUnlocked(false);
      } else {
        toast.error(err?.response?.data?.detail || "Error cargando datos");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!unlocked || !user?.is_super_admin) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked, user?.is_super_admin, statusFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.company_name || "").toLowerCase().includes(q) ||
      (u.full_name || "").toLowerCase().includes(q)
    );
  }, [users, query]);

  const openExtend = (u, mode) => {
    setExtendTarget({ user: u, mode });
    setExtendDays(mode === "reactivate" ? 3 : 7);
  };

  const confirmExtend = async () => {
    if (!extendTarget) return;
    const { user: u, mode } = extendTarget;
    setActing(true);
    try {
      const path = mode === "extend" ? "extend-trial" : "reactivate-trial";
      await api.post(`/admin/users/${u.id}/${path}`, { days: Number(extendDays) }, { headers: adminHeaders() });
      toast.success(mode === "extend" ? `+${extendDays} dias para ${u.email}` : `Trial reactivado ${extendDays} dias`);
      setExtendTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar trial");
    } finally {
      setActing(false);
    }
  };

  const setUnlimited = async (u) => {
    setActing(true);
    try {
      await api.post(`/admin/users/${u.id}/unlimited`, {}, { headers: adminHeaders() });
      toast.success(`${u.email} ahora tiene acceso ilimitado`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setActing(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setActing(true);
    try {
      await api.post(`/admin/users/${revokeTarget.id}/revoke`, {}, { headers: adminHeaders() });
      toast.success(`Acceso revocado para ${revokeTarget.email}`);
      setRevokeTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setActing(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(PIN_STORAGE_KEY);
    logout();
  };

  if (user && !user.is_super_admin) return null;
  if (!unlocked) return <PinGate onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-[#09090B] text-[#FAFAFA]" data-testid="admin-panel">
      <header className="border-b border-[#27272A] bg-[#09090B]/90 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <LogoMark size={36} />
              <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-[#DC2626] border-2 border-[#09090B] flex items-center justify-center">
                <ShieldStar size={8} weight="fill" className="text-white" />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Wazco Express · God mode</div>
              <h1 className="font-display text-lg font-medium tracking-tight">Panel super admin</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-[#A1A1AA] hidden sm:block">{user?.email}</div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="border-[#27272A] bg-[#121214] hover:bg-[#1F1F23] text-[#FAFAFA]"
            >
              <SignOut size={14} className="mr-2" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Tabs */}
        <nav className="flex gap-2 border-b border-[#27272A] -mb-2">
          {[
            { k: "users", l: "Usuarios", icon: UsersIcon },
            { k: "plans", l: "Planes", icon: Tag },
            { k: "leads", l: "Leads", icon: UserPlus },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`relative flex items-center gap-2 px-4 h-11 text-sm font-medium transition-colors ${
                tab === t.k ? "text-white" : "text-[#71717A] hover:text-[#A1A1AA]"
              }`}
            >
              <t.icon size={14} /> {t.l}
              {tab === t.k && <span className="absolute inset-x-2 bottom-0 h-0.5 bg-[#DC2626] rounded-full" />}
            </button>
          ))}
        </nav>

        {tab === "plans" && <AdminPlans headers={adminHeaders()} />}
        {tab === "leads" && <AdminLeads headers={adminHeaders()} />}

        {tab === "users" && (<>
        {/* Metrics */}
        <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MetricCard label="Workspaces" value={metrics?.total_workspaces ?? "-"} icon={ShieldStar} />
          <MetricCard label="Activos" value={metrics?.active_trials ?? "-"} tone="green" icon={Clock} />
          <MetricCard label="Expirados" value={metrics?.expired_trials ?? "-"} tone="red" icon={XCircle} />
          <MetricCard label="Ilimitados" value={metrics?.unlimited ?? "-"} tone="violet" icon={InfinityIcon} />
          <MetricCard label="Miembros equipo" value={metrics?.team_members ?? "-"} tone="amber" icon={CheckCircle} />
        </section>

        {/* Filters */}
        <section className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por email, empresa o nombre..."
              data-testid="admin-search-input"
              className="pl-9 bg-[#121214] border-[#27272A] focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626] h-10"
            />
          </div>
          <div className="flex gap-2">
            {[
              { k: "all", l: "Todos" },
              { k: "active", l: "Activos" },
              { k: "expired", l: "Expirados" },
              { k: "unlimited", l: "Ilimitados" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setStatusFilter(f.k)}
                data-testid={`filter-${f.k}`}
                className={`px-3 h-10 text-xs uppercase tracking-wider rounded-md border transition-colors ${
                  statusFilter === f.k
                    ? "bg-[#DC2626] border-[#DC2626] text-white"
                    : "bg-[#121214] border-[#27272A] text-[#A1A1AA] hover:text-[#FAFAFA]"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </section>

        {/* Users table */}
        <section className="bg-[#121214] border border-[#27272A] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0E0E10] border-b border-[#27272A]">
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                  <th className="px-4 py-3 font-normal">Email / Empresa</th>
                  <th className="px-4 py-3 font-normal">Estado</th>
                  <th className="px-4 py-3 font-normal hidden md:table-cell">Datos</th>
                  <th className="px-4 py-3 font-normal hidden lg:table-cell">Creado</th>
                  <th className="px-4 py-3 font-normal text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A]">
                {loading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-[#71717A]">Cargando...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-[#71717A]">Sin resultados</td></tr>
                ) : filtered.map((u) => (
                  <tr key={u.id} className="hover:bg-[#0E0E10]/60 transition-colors" data-testid={`admin-row-${u.email}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{u.email}</span>
                        {u.is_super_admin && (
                          <Badge className="bg-[#DC2626]/15 text-[#DC2626] border-[#DC2626]/30 text-[10px] gap-1">
                            <ShieldStar size={10} weight="fill" />ADMIN
                          </Badge>
                        )}
                        {u.auth_provider === "google" && (
                          <span className="text-[10px] uppercase tracking-wider text-[#71717A]">google</span>
                        )}
                      </div>
                      <div className="text-xs text-[#A1A1AA]">{u.company_name} · {u.full_name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={u.status} days={u.days_left} />
                      {u.trial_ends_at && (
                        <div className="text-[10px] text-[#71717A] mt-1 font-mono">
                          {new Date(u.trial_ends_at).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs text-[#A1A1AA]">
                      {u.contacts_count} contactos · {u.orders_count} ordenes · {u.members_count} miembros
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-[#71717A]">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 justify-end flex-wrap">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => openExtend(u, "extend")}
                          disabled={u.is_super_admin || acting}
                          data-testid={`btn-extend-${u.email}`}
                          className="h-8 border-[#27272A] bg-transparent hover:bg-[#1F1F23] text-[#22C55E] disabled:opacity-30"
                        >
                          <Lightning size={12} className="mr-1" />Extender
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => openExtend(u, "reactivate")}
                          disabled={u.is_super_admin || acting}
                          data-testid={`btn-reactivate-${u.email}`}
                          className="h-8 border-[#27272A] bg-transparent hover:bg-[#1F1F23] text-[#F59E0B] disabled:opacity-30"
                        >
                          <ArrowCounterClockwise size={12} className="mr-1" />Reactivar
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setUnlimited(u)}
                          disabled={u.is_super_admin || u.status === "unlimited" || acting}
                          data-testid={`btn-unlimited-${u.email}`}
                          className="h-8 border-[#27272A] bg-transparent hover:bg-[#1F1F23] text-[#A78BFA] disabled:opacity-30"
                        >
                          <InfinityIcon size={12} className="mr-1" />Ilimitado
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setRevokeTarget(u)}
                          disabled={u.is_super_admin || acting}
                          data-testid={`btn-revoke-${u.email}`}
                          className="h-8 border-[#27272A] bg-transparent hover:bg-[#1F1F23] text-[#EF4444] disabled:opacity-30"
                        >
                          <Prohibit size={12} className="mr-1" />Revocar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        </>)}
      </main>

      {/* Extend / Reactivate modal */}
      <Dialog open={!!extendTarget} onOpenChange={(o) => !o && setExtendTarget(null)}>
        <DialogContent className="bg-[#121214] border-[#27272A] text-[#FAFAFA]">
          <DialogHeader>
            <DialogTitle className="font-display">
              {extendTarget?.mode === "extend" ? "Extender trial" : "Reactivar trial"}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {extendTarget?.user?.email}
              {extendTarget?.mode === "extend"
                ? " · suma dias al final del trial actual (o desde ahora si ya expiro)."
                : " · reinicia el trial desde ahora."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-wider text-[#71717A]">Dias</Label>
            <Input
              type="number"
              min="1"
              max="3650"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              data-testid="extend-days-input"
              className="bg-[#09090B] border-[#27272A] focus:border-[#DC2626] h-11"
            />
            <div className="flex flex-wrap gap-2">
              {[3, 7, 15, 30, 90, 365].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setExtendDays(d)}
                  data-testid={`preset-${d}d`}
                  className={`px-3 h-8 text-xs rounded-md border ${
                    Number(extendDays) === d
                      ? "bg-[#DC2626] border-[#DC2626] text-white"
                      : "bg-[#09090B] border-[#27272A] text-[#A1A1AA] hover:text-[#FAFAFA]"
                  }`}
                >
                  {d >= 365 ? `${d/365}a` : `${d}d`}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setExtendTarget(null)}
              className="border-[#27272A] bg-transparent"
            >Cancelar</Button>
            <Button
              onClick={confirmExtend}
              disabled={acting || !extendDays || Number(extendDays) <= 0}
              data-testid="extend-confirm-button"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
            >
              {acting ? "Aplicando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirm */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent className="bg-[#121214] border-[#27272A] text-[#FAFAFA]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Revocar acceso</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              {revokeTarget?.email} sera bloqueado inmediatamente. Puede reactivarse despues.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#27272A] bg-transparent text-[#FAFAFA] hover:bg-[#1F1F23]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRevoke}
              data-testid="revoke-confirm-button"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
            >
              Revocar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
